import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Stack,
  Divider,
  Button,
  useTheme,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationRoadmap from './LocationRoadmap';

const Footer = () => {
  const theme = useTheme();

  const scrollToTop = () => {
    const element = document.getElementById('home');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        background:
          'linear-gradient(180deg, rgba(5,8,22,0) 0%, rgba(5,8,22,0.92) 18%, #050816 100%)',
        pt: 8,
        pb: 6,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main}50 50%, transparent 100%)`,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '20px',
          height: '20px',
          background: `${theme.palette.primary.main}20`,
          borderRadius: '50%',
          filter: 'blur(20px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '40px',
          height: '40px',
          background: `${theme.palette.secondary.main}15`,
          borderRadius: '50%',
          filter: 'blur(25px)',
        }}
      />

      <Container maxWidth="lg">
        <Box
          sx={{
            mb: 7,
            p: { xs: 3.25, md: 4.5 },
            borderRadius: 6,
            background:
              'linear-gradient(135deg, rgba(12,22,39,0.92) 0%, rgba(8,15,29,0.98) 100%)',
            border: '1px solid rgba(108, 154, 191, 0.18)',
            boxShadow: '0 28px 80px rgba(2, 6, 23, 0.42)',
            position: 'relative',
            overflow: 'hidden',
            isolation: 'isolate',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: { xs: -110, md: -80 },
              right: { xs: -90, md: -50 },
              width: { xs: 220, md: 260 },
              height: { xs: 220, md: 260 },
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(89,208,255,0.22) 0%, transparent 70%)',
              zIndex: 0,
            }}
          />
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontSize: '0.9rem',
                  display: 'inline-block',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  mb: 1.5,
                  lineHeight: 1.4,
                  pl: 0.4,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                İletişim
              </Typography>
              <Typography variant="h3" sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
                Kurumunuz için güvenli ve uygulanabilir bir KVKK yol haritası oluşturalım
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 680, position: 'relative', zIndex: 1 }}>
                İlk görüşmede mevcut süreçlerinizi, risk alanlarını ve teknik ihtiyaçları birlikte
                değerlendirip size uygun aksiyon planını netleştirelim.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="mailto:info@gncveri.com"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    minWidth: { xs: '100%', md: 220 },
                    color: '#06111f',
                    background: 'linear-gradient(180deg, #ffffff 0%, #dff6ff 100%)',
                  }}
                >
                  E-posta Gönderin
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="tel:+905550118743"
                  sx={{
                    minWidth: { xs: '100%', md: 220 },
                    color: 'common.white',
                    borderColor: 'rgba(255,255,255,0.16)',
                    bgcolor: 'rgba(255,255,255,0.03)',
                  }}
                >
                  Hemen Arayın
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <LocationRoadmap />

        <Grid container spacing={6}>
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src="/images/gnc-logo.png"
                alt="GNC Veri Teknolojileri"
                sx={{
                  display: 'block',
                  width: { xs: 240, sm: 280 },
                  maxWidth: '100%',
                  height: 'auto',
                  mb: 2,
                }}
              />
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, maxWidth: 320, lineHeight: 1.8 }}
              >
                KVKK uyumu ve bilgi güvenliği süreçlerinde kurumunuz için uygulanabilir,
                sürdürülebilir ve ölçülebilir danışmanlık sunuyoruz.
              </Typography>
              <Stack direction="row" spacing={2}>
                {[LinkedInIcon, TwitterIcon, FacebookIcon].map((Icon, index) => (
                  <IconButton
                    key={index}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(148,163,184,0.14)',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        transform: 'translateY(-3px)',
                        '& svg': { color: 'white' },
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <Icon sx={{ color: 'primary.main' }} />
                  </IconButton>
                ))}
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="h6" color="text.primary" gutterBottom fontWeight="600">
              HİZMETLERİMİZ
            </Typography>
            <Stack spacing={2}>
              {[
                'Siber Güvenlik Çözümleri',
                'Bilgi Güvenliği Danışmanlığı',
                'KVKK Danışmanlığı',
                'Netsis ERP Çözümleri',
                'Data Engineering & BI',
                'AI Solutions & Analytics',
                'Sanallaştırma ve Bulut',
                'Güvenlik Kamera Sistemleri',
                'Network Altyapı',
                'Eğitim Hizmetleri',
              ].map((service, index) => (
                <Link
                  key={index}
                  href="#services"
                  underline="none"
                  sx={{
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                      transform: 'translateX(8px)',
                    },
                    '&::before': {
                      content: '""',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      mr: 2,
                    },
                  }}
                >
                  {service}
                </Link>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="h6" color="text.primary" gutterBottom fontWeight="600">
              İLETİŞİM BİLGİLERİ
            </Typography>
            <Stack spacing={3}>
              {[
                { icon: EmailIcon, text: 'info@gncveri.com' },
                { icon: PhoneIcon, text: '+90 (555) 011 87 43' },
                {
                  icon: LocationOnIcon,
                  text: 'Başpınar OSB Mah. OSB 1. Bölge 83101 Nolu Cadde No:17 (İpek İş Merkezi B Blok Numara 9) Şehitkamil / Gaziantep',
                  multiline: true,
                },
              ].map((item, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={2}
                  alignItems={item.multiline ? 'flex-start' : 'center'}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(148, 163, 184, 0.12)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 18px 40px rgba(2, 6, 23, 0.35)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(89, 208, 255, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <item.icon sx={{ color: 'primary.main' }} />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: item.multiline ? 1.6 : 1.5 }}
                  >
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 6, opacity: 0.1 }} />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              opacity: 0.8,
              fontSize: '0.875rem',
            }}
          >
            {new Date().getFullYear()} GNC Veri Teknolojileri. Tüm hakları saklıdır.
          </Typography>
          <Button
            variant="text"
            onClick={scrollToTop}
            sx={{ color: 'text.secondary', px: 0, minWidth: 0 }}
          >
            Başa Dön
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
